import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { Server } from '@wajeez/wajeez.microservices';

import {
  getComponentById,
  getAllowedComponentsForCollectionComponentType,
  getComponentLookups,
  getComponentLookupsByComponentTypeIds,
  createComponentByTypeId,
  editComponent,
  cloneComponent,
  getComponentComponentLookups,
  getComponentPageBodyLookups,
  getComponentsDatatable,
  getComponentByIdMultilingual,
  createComponentByTypeIdMultilingual,
  editComponentMultilingual,
} from './thunks';
import { ComponentsState } from './types';

export const name = 'Components';

const initialState: ComponentsState = {
  componentsDatatable: {
    data: null,
    status: 'idle',
  },
  details: null,
  detailsHistory: [],
  detailsStatus: 'idle',
  allowedComponentsForCollectionComponentType: {
    data: [],
    meta: { currentPage: undefined, totalCount: undefined, hasNext: undefined },
    status: 'idle',
  },
  componentLookups: {
    data: [],
    status: 'idle',
  },
  componentComponentLookups: {
    data: [],
    status: 'idle',
  },
  componentPageBodyLookups: {
    data: [],
    status: 'idle',
  },
  componentLookupsByComponentTypeId: { status: 'idle', typeIds: {} },
  createComponentByTypeId: {
    status: 'idle',
    data: null,
  },
  editComponent: {
    status: 'idle',
    data: null,
  },
  cloneComponent: {
    status: 'idle',
    data: null,
  },
  byIdMultilingual: {
    data: null,
    status: 'idle',
  },
  createMultilingual: {
    data: null,
    status: 'idle',
  },
  editMultilingual: {
    data: null,
    status: 'idle',
  },
};

export const getComponentsDatatableAsync = createAsyncThunk(
  name + '/ getComponentsDatatable',
  getComponentsDatatable
);

export const getComponentByIdAsync = createAsyncThunk(
  name + '/ getComponentById',
  getComponentById
);

export const getAllowedComponentsForCollectionComponentTypeAsync =
  createAsyncThunk(
    name + '/ getAllowedComponentsForCollectionComponentType',
    getAllowedComponentsForCollectionComponentType
  );

export const getComponentLookupsAsync = createAsyncThunk(
  name + '/ getComponentLookups',
  getComponentLookups
);

export const getComponentComponentLookupsAsync = createAsyncThunk(
  name + '/ getComponentComponentLookups',
  getComponentComponentLookups
);

export const getComponentPageBodyLookupsAsync = createAsyncThunk(
  name + '/ getComponentPageBodyLookups',
  getComponentPageBodyLookups
);

export const getComponentLookupsByComponentTypeIdsAsync = createAsyncThunk(
  name + '/ getComponentLookupsByComponentTypeIds',
  getComponentLookupsByComponentTypeIds
);

export const createComponentByTypeIdAsync = createAsyncThunk(
  name + '/ createComponentByTypeId',
  createComponentByTypeId
);

export const editComponentAsync = createAsyncThunk(
  name + '/ editComponent',
  editComponent
);

export const cloneComponentAsync = createAsyncThunk(
  name + '/ cloneComponent',
  cloneComponent
);

export const getComponentByIdMultilingualAsync = createAsyncThunk(
  name + '/ getComponentByIdMultilingual',
  getComponentByIdMultilingual
);

export const createComponentByTypeIdMultilingualAsync = createAsyncThunk(
  name + '/ createComponentByTypeIdMultilingual',
  createComponentByTypeIdMultilingual
);

export const editComponentMultilingualAsync = createAsyncThunk(
  name + '/ editComponentMultilingual',
  editComponentMultilingual
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearAllowedComponentsForCollectionComponentType: (state) => {
      state.allowedComponentsForCollectionComponentType =
        initialState.allowedComponentsForCollectionComponentType;
    },
    clearComponentById: (state) => {
      state.details = null;
    },
    clearComponentByIdMultilingual: (state) => {
      state.byIdMultilingual = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getComponentsDatatableAsync.pending, (state) => {
        state.componentsDatatable.status = 'loading';
      })
      .addCase(getComponentsDatatableAsync.fulfilled, (state, action) => {
        state.componentsDatatable = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentsDatatableAsync.rejected, (state) => {
        state.componentsDatatable.status = 'failed';
      })
      .addCase(getComponentByIdAsync.pending, (state) => {
        state.detailsStatus = 'loading';
      })
      .addCase(getComponentByIdAsync.fulfilled, (state, action) => {
        state.detailsStatus = 'idle';
        state.details = action.payload;
        if (
          state.detailsHistory.find((d) => d.id === action.payload.id) ===
          undefined
        )
          state.detailsHistory.push(action.payload);
      })
      .addCase(getComponentByIdAsync.rejected, (state) => {
        state.detailsStatus = 'failed';
      })
      .addCase(
        getAllowedComponentsForCollectionComponentTypeAsync.pending,
        (state) => {
          state.allowedComponentsForCollectionComponentType.status = 'loading';
        }
      )
      .addCase(
        getAllowedComponentsForCollectionComponentTypeAsync.fulfilled,
        (state, action) => {
          const stateObj = _.reduce(
            state.allowedComponentsForCollectionComponentType.data,
            (result, component) => {
              result[component.id!] = component;

              return result;
            },
            {} as { [key: string]: Server.ComponentOutput }
          );

          const payloadObj = _.reduce(
            action.payload.data,
            (result, component) => {
              result[component.id!] = component;

              return result;
            },
            {} as { [key: string]: Server.ComponentOutput }
          );

          const newStateObj = { ...stateObj, ...payloadObj };

          state.allowedComponentsForCollectionComponentType = {
            data: Object.values(newStateObj),
            meta: action.payload.meta,
            status: 'idle',
          };
        }
      )
      .addCase(
        getAllowedComponentsForCollectionComponentTypeAsync.rejected,
        (state) => {
          state.allowedComponentsForCollectionComponentType.status = 'failed';
        }
      )
      .addCase(getComponentLookupsAsync.pending, (state) => {
        state.componentLookups.status = 'loading';
      })
      .addCase(getComponentLookupsAsync.fulfilled, (state, action) => {
        state.componentLookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentLookupsAsync.rejected, (state) => {
        state.componentLookups.status = 'failed';
      })
      .addCase(getComponentComponentLookupsAsync.pending, (state) => {
        state.componentComponentLookups.status = 'loading';
      })
      .addCase(getComponentComponentLookupsAsync.fulfilled, (state, action) => {
        state.componentComponentLookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentComponentLookupsAsync.rejected, (state) => {
        state.componentComponentLookups.status = 'failed';
      })
      .addCase(getComponentPageBodyLookupsAsync.pending, (state) => {
        state.componentPageBodyLookups.status = 'loading';
      })
      .addCase(getComponentPageBodyLookupsAsync.fulfilled, (state, action) => {
        state.componentPageBodyLookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentPageBodyLookupsAsync.rejected, (state) => {
        state.componentPageBodyLookups.status = 'failed';
      })
      .addCase(
        getComponentLookupsByComponentTypeIdsAsync.pending,
        (state, action) => {
          state.componentLookupsByComponentTypeId.status = 'loading';
          state.componentLookupsByComponentTypeId.typeIds[
            JSON.stringify(action.meta.arg.componentTypeIds)
          ] = {
            data: [],
            status: 'loading',
          };
        }
      )
      .addCase(
        getComponentLookupsByComponentTypeIdsAsync.fulfilled,
        (state, action) => {
          state.componentLookupsByComponentTypeId.status = 'idle';
          state.componentLookupsByComponentTypeId.typeIds[
            JSON.stringify(action.meta.arg.componentTypeIds)
          ] = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getComponentLookupsByComponentTypeIdsAsync.rejected,
        (state, action) => {
          state.componentLookupsByComponentTypeId.status = 'failed';
          state.componentLookupsByComponentTypeId.typeIds[
            JSON.stringify(action.meta.arg.componentTypeIds)
          ] = {
            data: [],
            status: 'failed',
          };
        }
      )
      .addCase(createComponentByTypeIdAsync.pending, (state) => {
        state.createComponentByTypeId.status = 'loading';
      })
      .addCase(createComponentByTypeIdAsync.fulfilled, (state, action) => {
        state.createComponentByTypeId = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(createComponentByTypeIdAsync.rejected, (state) => {
        state.createComponentByTypeId.status = 'failed';
      })
      .addCase(editComponentAsync.pending, (state) => {
        state.editComponent.status = 'loading';
      })
      .addCase(editComponentAsync.fulfilled, (state, action) => {
        state.editComponent = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(editComponentAsync.rejected, (state) => {
        state.editComponent.status = 'failed';
      })
      .addCase(cloneComponentAsync.pending, (state) => {
        state.cloneComponent.status = 'loading';
      })
      .addCase(cloneComponentAsync.fulfilled, (state, action) => {
        state.cloneComponent = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(cloneComponentAsync.rejected, (state) => {
        state.cloneComponent.status = 'failed';
      })
      .addCase(getComponentByIdMultilingualAsync.pending, (state) => {
        state.byIdMultilingual.status = 'loading';
      })
      .addCase(getComponentByIdMultilingualAsync.fulfilled, (state, action) => {
        state.byIdMultilingual = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentByIdMultilingualAsync.rejected, (state) => {
        state.byIdMultilingual.status = 'failed';
      })
      .addCase(createComponentByTypeIdMultilingualAsync.pending, (state) => {
        state.createMultilingual.status = 'loading';
      })
      .addCase(
        createComponentByTypeIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.createMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(createComponentByTypeIdMultilingualAsync.rejected, (state) => {
        state.createMultilingual.status = 'failed';
      })
      .addCase(editComponentMultilingualAsync.pending, (state) => {
        state.editMultilingual.status = 'loading';
      })
      .addCase(editComponentMultilingualAsync.fulfilled, (state, action) => {
        state.editMultilingual = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(editComponentMultilingualAsync.rejected, (state) => {
        state.editMultilingual.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
